import type { CSSProperties } from 'react';
import type { BridgeCard, BridgeSuit } from '@strategy4kids/game-engine';
import { cardId } from '@strategy4kids/game-engine';
import { cn } from '@strategy4kids/ui';
import { useT } from '../i18n/useT.js';
import { useCardDesignStore, type CardDesign } from '../state/card-design-store.js';
import { useCardFaceStore } from '../state/card-face-store.js';
import { CardFace } from './CardFace.js';

/** Card width scale, one place: big enough to read the face on a phone, bigger again on tablets and desktops. */
export const CARD_WIDTH_CLASS = 'w-[4.5rem] min-[480px]:w-24 lg:w-28 xl:w-32';
/** An empty slot the size of a face-up card (a trick position with nothing played yet). */
/** Cards in the middle of the table (the trick) are one step smaller so the four of them stay a compact cluster. */
export const CARD_WIDTH_COMPACT_CLASS = 'w-[3.5rem] min-[480px]:w-[4.5rem] lg:w-24 xl:w-28';
export const CARD_SLOT_CLASS = `block aspect-[5/7] shrink-0 ${CARD_WIDTH_COMPACT_CLASS}`;

/**
 * A hand of cards. From `sm` up it wraps with a small gap; on a phone the cards overlap into a fan so
 * thirteen big cards fit in two short rows instead of a tall column. The overlap is 28px on a 72px card, so every
 * card keeps 44px of its own width (its centre included) as a tap target; later cards sit on top of earlier ones.
 */
export const HAND_CLASS =
  'flex w-full max-w-full flex-wrap justify-center gap-1 max-sm:justify-start max-sm:gap-x-0 max-sm:gap-y-2 max-sm:pl-7 max-sm:[&>*]:-ml-7 lg:flex-nowrap lg:gap-0 lg:[&>*:not(:first-child)]:-ml-11 xl:[&>*:not(:first-child)]:-ml-10 2xl:[&>*:not(:first-child)]:-ml-6';

export const SUIT_SYMBOL: Record<BridgeSuit, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const RANK_TEXT: Record<number, string> = { 11: 'J', 12: 'Q', 13: 'K', 14: 'A', 15: '★' };
export const rankText = (rank: number): string => RANK_TEXT[rank] ?? String(rank);

/**
 * Suit colours. The default deck is the usual two (red hearts and diamonds, dark spades and clubs).
 * The four-colour deck gives every suit its own colour for players who cannot tell red from black
 * apart; the suit SYMBOL is always printed too, so colour is never the only cue.
 */
const SUIT_COLOUR = {
  standard: { S: 'text-slate-900', C: 'text-slate-900', H: 'text-red-600', D: 'text-red-600' },
  fourColour: { S: 'text-slate-900', C: 'text-green-700', H: 'text-red-600', D: 'text-blue-700' },
} as const;

interface PlayingCardProps {
  card: BridgeCard;
  fourColour?: boolean;
  /** Present = the card is a button. */
  onPlay?: () => void;
  /** Drawn but not tappable right now (dimmed). */
  dimmed?: boolean;
  /** Ringed, e.g. the winner of the last trick. */
  highlight?: boolean;
  /** For a card you pick and unpick (Rummy): ringed, and announced as pressed. Leave undefined for a plain play-it button. */
  selected?: boolean;
  /** The smaller trick-area size. */
  compact?: boolean;
}

/** A face-up card: DOM, not canvas, so it is a real button with a spoken name ("Ace of spades"). */
export function PlayingCard({ card, fourColour = false, onPlay, dimmed = false, highlight = false, selected, compact = false }: PlayingCardProps) {
  const { t } = useT('games');
  // A Joker has no rank or suit to speak: it is the red or the black Joker (its suit carries the colour).
  const label = card.rank === 15 ? t(card.suit === 'H' || card.suit === 'D' ? 'bridge-joker-red' : 'bridge-joker-black') : t('bridge-card-label', { rank: t(`bridge-rank-${card.rank}`), suit: t(`bridge-suit-${card.suit}`) });
  const colour = SUIT_COLOUR[fourColour ? 'fourColour' : 'standard'][card.suit];
  const className = cn(
    'relative block aspect-[5/7] shrink-0 select-none overflow-hidden rounded-xl border bg-white font-display leading-none shadow-md', compact ? CARD_WIDTH_COMPACT_CLASS : CARD_WIDTH_CLASS,
    colour,
    highlight || selected ? 'border-accent ring-2 ring-accent' : 'border-slate-300',
    selected && '-translate-y-2',
    dimmed && 'brightness-90 saturate-50',
    onPlay &&
      'cursor-pointer transition-transform hover:-translate-y-1 focus-visible:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  );
  const faceStyle = useCardFaceStore((s) => s.style);
  const face = <CardFace rank={card.rank} suit={card.suit} style={faceStyle} className="block size-full" />;
  if (onPlay) {
    return (
      <button type="button" className={className} aria-label={label} aria-pressed={selected} data-testid={`bridge-card-${cardId(card)}`} data-card={cardId(card)} onClick={onPlay}>
        {face}
      </button>
    );
  }
  return (
    <div role="img" className={className} aria-label={label} data-card={cardId(card)}>
      {face}
    </div>
  );
}

/**
 * Card back patterns, pure CSS (no assets). `classic` is the plain sky card every game has always
 * shown; the others are repeating gradients sized to stay readable at back dimensions.
 */
const light = (fraction: string) => `color-mix(in srgb, var(--color-sky) ${fraction}, white)`;

/** Card back patterns, pure CSS (no assets). Classic is the plain sky card every game has always shown. */
export const CARD_BACK_STYLE: Record<CardDesign, CSSProperties> = {
  // The plain sky card, now with a fine diamond lattice and a white inner frame so a big back is not a flat block.
  classic: {
    backgroundImage: `repeating-linear-gradient(45deg, transparent 0 5px, ${light('25%')} 5px 6px), repeating-linear-gradient(-45deg, transparent 0 5px, ${light('25%')} 5px 6px)`,
    boxShadow: 'inset 0 0 0 3px rgba(255,255,255,0.85), inset 0 0 0 4px var(--color-sky)',
  },
  stripes: { backgroundImage: `repeating-linear-gradient(45deg, var(--color-sky) 0 4px, ${light('45%')} 4px 8px)` },
  dots: {
    backgroundImage: `radial-gradient(${light('40%')} 1.5px, transparent 1.5px)`,
    backgroundSize: '6px 6px',
    backgroundColor: 'var(--color-sky)',
  },
  waves: { backgroundImage: `repeating-linear-gradient(0deg, var(--color-sky) 0 5px, ${light('45%')} 5px 7px)` },
  // The one image back: generated theme art (scripts/assets/pirate.images.json), cover-fit.
  pirate: {
    backgroundImage: 'url(/themes/pirate/card-back.webp)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: 'var(--color-sky)',
  },
};

/** A card back, for a hand that is hidden from you. The design is the per-device card-back preference. */
export function CardBack({ className }: { className?: string }) {
  const design = useCardDesignStore((s) => s.design);
  return (
    <span
      aria-hidden="true"
      style={CARD_BACK_STYLE[design]}
      className={cn('block aspect-[5/7] w-10 shrink-0 rounded-md border border-slate-300 bg-sky shadow-sm min-[480px]:w-12 lg:w-14', className)}
    />
  );
}
